/**
 * @file PostAbaCases.jsx
 * @description Standalone SEO/conversion page at /post-aba-cases — invites ABA
 *   agencies to post open cases and get set up on the platform. Honest about
 *   pre-launch scale: this is an invitation to post and join, not a claim that
 *   a large live marketplace of open cases or providers already exists. No
 *   fake case examples, no JobPosting schema, no case/provider counts, no
 *   competitor names, no pay figures. Mirrors the structural/stylistic
 *   template established by AboutAbaCases.jsx.
 * @module components/PostAbaCases
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfigVersion } from "../lib/useConfigVersion";
import { T } from "../theme";
import {
  BriefcaseIcon, TrendingUpIcon, SearchIcon, UsersIcon,
  CheckIcon, ShieldIcon, ClockIcon, DollarIcon,
} from "./Icons";
import { ProofChips, FlowSteps, IconTiles, CasePreview, FounderCard, OutsideFloridaNote, agencyChips } from "./AgencyVisuals";
import CasePromotion from "./CasePromotion";
import PublicFooter from "./PublicFooter";
import ContactModal from "./ContactModal";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta, landingMeta, trackLandingView } from "../lib/analytics";
import { getStarterPromoDates } from "../lib/plans";
import StickyMobileCta from "./StickyMobileCta";
import AgencyPricingNote from "./AgencyPricingNote";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";
const FLOW_ICONS = [BriefcaseIcon, SearchIcon, UsersIcon, CheckIcon];
const TILE_ICONS = [ShieldIcon, TrendingUpIcon, ClockIcon, DollarIcon];

export default function PostAbaCases({ onNavigate }) {
  useConfigVersion(); // re-render with the Admin prices once platform_config loads
  const { t, i18n } = useTranslation();
  const [contactOpen, setContactOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { trackLandingView("post_aba_cases"); }, []);

  usePageSeo({
    title: "Post ABA Cases and Find RBTs, BCBAs, and ABA Therapists | ABAcases",
    description: "Post your open ABA cases on ABAcases and reach verified RBTs, BTs, BCBAs, and BCaBAs by location, schedule, credential, and setting.",
    canonical: "/post-aba-cases",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-post-aba-cases", [
    { name: "Home", url: "/" },
    { name: "For ABA Agencies", url: "/aba-agencies" },
    { name: "Post ABA Cases", url: "/post-aba-cases" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goPostCase = (position) => {
    track(A.AGENCY_SIGNUP_CLICK, {
      supabaseMeta: landingMeta("post_aba_cases", { cta_position: position }),
      ga4Params: ctaMeta({ pagePath: "/post-aba-cases/", pageType: "agency", ctaLabel: "Post a case free", targetPath: "/auth", userLanguage: i18n.language, audience: "agency" }),
    });
    setSignupIntent("agency");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  const promoActive = getStarterPromoDates().isPromoActive;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('postAbaCases.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('postAbaCases.breadcrumbForAgencies'), onClick: () => onNavigate("aba-agencies") },
            { label: t('postAbaCases.breadcrumbPostCases') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('postAbaCases.badgeForAgencies')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('postAbaCases.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 24px" }}>
            {t('postAbaCases.heroSubtitle')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => goPostCase("hero")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BriefcaseIcon size={16} color="#fff" /> {promoActive ? t('agencyPricing.cta') : t('agencyPricing.ctaPaid')}
            </button>
          </div>
          <AgencyPricingNote style={{ color: "#cbd5e1", maxWidth: 560, marginTop: 14, fontSize: 14 }} />
          <div style={{ marginTop: 20 }}>
            <ProofChips dark items={agencyChips(t)} />
          </div>
        </div>
      </header>

      {/* ── How posting works (flow) + one SEO paragraph ─────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 20px", letterSpacing: "-0.02em", textAlign: "center" }}>{t('postAbaCases.section1Title')}</h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.7, maxWidth: 720, margin: "0 auto 32px", textAlign: "center" }}>{t('postAbaCases.section1Body1')}</p>
          <FlowSteps isMobile={isMobile} steps={FLOW_ICONS.map((Icon, i) => ({ Icon, title: t(`agencyLanding.flow${i + 1}Title`), sub: t(`agencyLanding.flow${i + 1}Sub`) }))} />
        </div>
      </section>

      {/* ── We help fill your case ───────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>{t("agencyVisuals.promoTitle")}</h2>
          <CasePromotion isMobile={isMobile} />
          <OutsideFloridaNote isMobile={isMobile} />
        </div>
      </section>

      {/* ── What providers see vs what stays private ─────────── */}
      <section style={{ padding: sectionPad }}>
        <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 20px", letterSpacing: "-0.02em", textAlign: "center" }}>{t('postAbaCases.section4Title')}</h2>
        <CasePreview isMobile={isMobile} />
      </section>

      {/* ── Why post here (tiles) + markets ───────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 20px", letterSpacing: "-0.02em", textAlign: "center" }}>{t('postAbaCases.section2Title')}</h2>
          <IconTiles isMobile={isMobile} items={TILE_ICONS.map((Icon, i) => ({ Icon, title: t(`agencyMarket.tile${i + 1}Title`), sub: t(`agencyMarket.tile${i + 1}Sub`) }))} />
          <p style={{ textAlign: "center", fontSize: 14, color: TEXT_MID, marginTop: 24, lineHeight: 1.9 }}>
            {t('agencyMarket.related')}{" "}
            <SeoLink to="aba-agencies" style={{ color: INDIGO, fontWeight: 700 }}>{t('postAbaCases.section7LinkAgencies')}</SeoLink>{" · "}
            <SeoLink to="aba-staffing-florida" style={{ color: INDIGO, fontWeight: 700 }}>{t('postAbaCases.section7LinkFlorida')}</SeoLink>{" · "}
            <SeoLink to="rbt-staffing-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t('postAbaCases.section7LinkMiami')}</SeoLink>{" · "}
            <SeoLink to="rbt-staffing-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t('postAbaCases.section7LinkCharlotte')}</SeoLink>{" · "}
            <SeoLink to="about-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('postAbaCases.section7LinkAbout')}</SeoLink>
          </p>
        </div>
      </section>

      {/* Founder */}
      <section style={{ padding: sectionPad, borderTop: `1px solid ${BORDER}` }}>
        <FounderCard isMobile={isMobile} onMessage={() => setContactOpen(true)} />
      </section>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      <PublicFooter />
      <StickyMobileCta label={promoActive ? t("agencyPricing.cta") : t("agencyPricing.ctaPaid")} onClick={() => goPostCase("sticky")} ctaId="agency" />
    </div>
  );
}
